import { useState } from "react";
import { Copy, Check, Storefront } from "@phosphor-icons/react";

// Platform-specific field templates in the exact order they appear on each platform
const PLATFORM_TEMPLATES = {
  eBay: {
    name: "eBay",
    fields: [
      { label: "Title", key: "title", maxLength: 80 },
      { label: "Category", key: "category" },
      { label: "Condition", key: "condition_ebay" },
      { label: "Condition Description", key: "condition_desc" },
      { label: "Brand", key: "brand" },
      { label: "Size", key: "size" },
      { label: "Color", key: "color" },
      { label: "Description", key: "description" },
      { label: "Price (Buy It Now)", key: "price" },
      { label: "Shipping", key: "shipping" },
    ],
  },
  Poshmark: {
    name: "Poshmark",
    fields: [
      { label: "Title", key: "title", maxLength: 80 },
      { label: "Description", key: "description_posh" },
      { label: "Category", key: "category_posh" },
      { label: "Size", key: "size" },
      { label: "Brand", key: "brand" },
      { label: "Original Retail Price", key: "original_price" },
      { label: "Listing Price", key: "price" },
      { label: "Color", key: "color" },
      { label: "Style Tags", key: "style_tags" },
    ],
  },
  Mercari: {
    name: "Mercari",
    fields: [
      { label: "Title", key: "title", maxLength: 80 },
      { label: "Description", key: "description_merc" },
      { label: "Category", key: "category" },
      { label: "Brand", key: "brand" },
      { label: "Condition", key: "condition_merc" },
      { label: "Color", key: "color" },
      { label: "Price", key: "price" },
      { label: "Shipping Weight", key: "shipping_weight" },
    ],
  },
  Depop: {
    name: "Depop",
    fields: [
      { label: "Description", key: "description_depop" },
      { label: "Category", key: "category" },
      { label: "Brand", key: "brand" },
      { label: "Size", key: "size" },
      { label: "Condition", key: "condition_depop" },
      { label: "Color", key: "color" },
      { label: "Price", key: "price" },
    ],
  },
  "Facebook Marketplace": {
    name: "Facebook Marketplace",
    fields: [
      { label: "Title", key: "title" },
      { label: "Price", key: "price" },
      { label: "Category", key: "category_fb" },
      { label: "Condition", key: "condition_fb" },
      { label: "Description", key: "description_fb" },
    ],
  },
};

function generateFieldValues(verdict, platform) {
  const brand = verdict.brand || "Unknown";
  const title = verdict.title_keywords || "";
  const size = verdict.size || "See photos";
  const condition = verdict.condition || "Good";
  const category = verdict.category || "clothing";
  const priceHigh = verdict.list_price_high || verdict.list_price_low || 0;
  const priceLow = verdict.list_price_low || 0;
  const isNwt = verdict.is_nwt;

  const conditionShort = isNwt
    ? "New with tags"
    : condition.toLowerCase().includes("excellent")
    ? "Like New"
    : condition.toLowerCase().includes("minor")
    ? "Good"
    : "Good";

  // Category mapping
  const catMap = {
    clothing: { ebay: "Clothing, Shoes & Accessories", posh: "Women > Tops" , merc: "Women > Tops", depop: "Tops", fb: "Clothing & Shoes" },
    shoes: { ebay: "Clothing, Shoes & Accessories > Shoes", posh: "Women > Shoes", merc: "Women > Shoes", depop: "Shoes", fb: "Clothing & Shoes" },
    accessories: { ebay: "Clothing, Shoes & Accessories > Accessories", posh: "Women > Accessories", merc: "Women > Accessories", depop: "Accessories", fb: "Clothing & Shoes" },
  };
  const cats = catMap[category] || catMap.clothing;

  // Color extraction from title
  const colors = ["Black", "White", "Blue", "Red", "Green", "Navy", "Gray", "Grey", "Pink", "Brown", "Beige", "Purple", "Orange", "Yellow", "Cream", "Burgundy", "Teal", "Olive"];
  const detectedColor = colors.find((c) => title.toLowerCase().includes(c.toLowerCase())) || "See photos";

  const originalPrice = Math.round(priceHigh * 2.5);

  const values = {
    title: title.slice(0, 80),
    brand,
    size,
    color: detectedColor,
    price: `$${priceHigh}`,
    original_price: `$${originalPrice}`,

    // eBay specific
    category: cats.ebay,
    condition_ebay: isNwt ? "New with tags" : "Pre-owned",
    condition_desc: condition,
    description: `${title}\n\n${brand} | Size: ${size} | Condition: ${condition}\n${isNwt ? "NEW WITH TAGS - Never worn!\n" : ""}\nShips fast! Check out my other listings for bundle deals.`,
    shipping: "Calculated or Free (build into price)",

    // Poshmark specific
    category_posh: cats.posh,
    description_posh: `${title}\n\n${brand}\nSize: ${size}\nCondition: ${conditionShort}${isNwt ? " - New With Tags!" : ""}\n${condition}\n\nSmoke-free home. Ships next business day!\n\n#${brand.replace(/\s/g, "")} #${category} #thrift`,
    style_tags: `${brand}, ${category}, ${verdict.season || "year-round"}`,

    // Mercari specific
    description_merc: `${title}\n\nBrand: ${brand}\nSize: ${size}\nCondition: ${conditionShort}${isNwt ? " - NWT" : ""}\n${condition}\n\nShips within 1 business day!`,
    condition_merc: isNwt ? "New" : conditionShort === "Like New" ? "Like New" : "Good",
    shipping_weight: category === "shoes" ? "1-2 lbs" : "Under 1 lb",

    // Depop specific
    description_depop: `${title}\n\n${brand} | ${size}\n${conditionShort}${isNwt ? " with tags" : ""}\n${condition}\n\nFree shipping on bundles! DM for questions`,
    condition_depop: isNwt ? "Brand New" : conditionShort === "Like New" ? "Like New" : "Used - Excellent",

    // Facebook Marketplace specific
    category_fb: cats.fb,
    condition_fb: isNwt ? "New" : "Used - Like New",
    description_fb: `${title}\n\n${brand} | Size: ${size}\nCondition: ${conditionShort}${isNwt ? " - NWT" : ""}\n${condition}\n\nLocal pickup or shipping available. No trades.`,
  };

  return values;
}

function CopyField({ label, value, maxLength }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="border-2 border-black" data-testid={`listing-field-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between px-3 py-1.5 bg-neutral-100 border-b border-black">
        <span className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-600">
          {label}
          {maxLength && <span className="text-neutral-400 normal-case tracking-normal"> (max {maxLength})</span>}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2 py-0.5 text-xs font-body font-semibold uppercase tracking-wide hover:bg-neutral-200 transition-colors"
          data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}
        >
          {copied ? (
            <>
              <Check size={12} weight="bold" className="text-green-600" />
              <span className="text-green-600">Copied</span>
            </>
          ) : (
            <>
              <Copy size={12} weight="bold" />
              Copy
            </>
          )}
        </button>
      </div>
      <div className="p-3 font-body text-sm whitespace-pre-wrap break-words">
        {value}
      </div>
    </div>
  );
}

export default function ListingTemplate({ verdict }) {
  const platform = verdict.best_platform || "eBay";
  const [activePlatform, setActivePlatform] = useState(platform);
  const [copiedAll, setCopiedAll] = useState(false);

  const template = PLATFORM_TEMPLATES[activePlatform] || PLATFORM_TEMPLATES["eBay"];
  const values = generateFieldValues(verdict, activePlatform);

  const handleCopyAll = async () => {
    const allText = template.fields
      .map((f) => `${f.label}:\n${values[f.key] || "—"}`)
      .join("\n\n---\n\n");
    try {
      await navigator.clipboard.writeText(allText);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = allText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const platforms = Object.keys(PLATFORM_TEMPLATES);

  return (
    <div className="space-y-3" data-testid="listing-template">
      {/* Platform Tabs */}
      <div className="flex overflow-x-auto gap-1 pb-1 -mx-1 px-1">
        {platforms.map((p) => (
          <button
            key={p}
            onClick={() => { setActivePlatform(p); setCopiedAll(false); }}
            className={`shrink-0 px-3 py-2 border-2 border-black font-body text-xs font-semibold uppercase tracking-wide transition-all ${
              activePlatform === p
                ? "bg-black text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] translate-x-0"
                : "bg-white text-black hover:bg-neutral-100"
            } ${p === platform ? "ring-2 ring-[#00E676]" : ""}`}
            data-testid={`platform-tab-${p.toLowerCase().replace(/\s/g, '-')}`}
          >
            {p === platform && <span className="mr-1">*</span>}
            {p}
          </button>
        ))}
      </div>

      {/* Recommended badge */}
      {activePlatform === platform && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00E676] border-2 border-black font-body text-xs font-semibold">
          <Storefront size={14} weight="bold" />
          Recommended platform for this item
        </div>
      )}

      {/* Fields */}
      <div className="space-y-2">
        {template.fields.map((field) => (
          <CopyField
            key={field.key}
            label={field.label}
            value={values[field.key] || "—"}
            maxLength={field.maxLength}
          />
        ))}
      </div>

      {/* Copy All */}
      <button
        onClick={handleCopyAll}
        className="w-full p-3 bg-black text-white font-heading font-bold uppercase tracking-wide border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[0px_0px_0px_0px_rgba(0,0,0,1)] transition-all flex items-center justify-center gap-2"
        data-testid="copy-all-fields-btn"
      >
        {copiedAll ? (
          <>
            <Check size={18} weight="bold" />
            All Fields Copied!
          </>
        ) : (
          <>
            <Copy size={18} weight="bold" />
            Copy All Fields
          </>
        )}
      </button>
    </div>
  );
}
